import React, { useState, useCallback } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { IconButton, Tooltip as MuiTooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';
import JsonSyntaxHighlighter from '../sandbox/JsonSyntaxHighlighter';
import ResourceTable from '../sandbox/ResourceTable';
import WarehouseLocations from '../sandbox/WarehouseLocations';
import { STATUS_BADGE, STATUS_LABEL } from '../sandbox/sandboxConfig';

function getRowTitle(row) {
  return row.order_number || row.purchase_order_number
    || row.invoice_number || row.sku || row.name || row.file_name || row.id;
}

const EXPAND_DEPTH = { true: 999, false: 1, null: 2 };

function ObjectResponsePanel({
  row, resourceKey, apiPath, connectionId, loading, noResults, hasResults,
}) {
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [viewMode, setViewMode] = useState('json');
  const [expandState, setExpandState] = useState(null);
  const [expandKey, setExpandKey] = useState(0);

  const toggleExpandAll = useCallback(() => {
    setExpandState((prev) => prev !== true);
    setExpandKey((k) => k + 1);
  }, []);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-100/60">
        <ClipLoader size={28} color="#a8a29e" />
      </div>
    );
  }

  if (!row && noResults) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-100/60">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-surface-200/60 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
          </div>
          <p className="text-base font-medium text-surface-600 mb-1">No results found</p>
          <p className="text-sm text-surface-400">Try a different query or check that the value is correct</p>
        </div>
      </div>
    );
  }

  if (!row && hasResults) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-100/60">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-surface-100 flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-surface-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
          <p className="text-sm text-surface-400">Select an object to view its API response</p>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-100/60 px-12">
        <div className="max-w-lg">
          <div className="w-12 h-12 rounded-xl bg-surface-200/60 flex items-center justify-center mb-5">
            <svg className="w-6 h-6 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-surface-800 mb-3">Production Data Explorer</h3>
          <p className="text-base text-surface-500 leading-relaxed mb-7">
            Quickly inspect and verify the normalized data returned by our APIs - no code required.
            Here's how it works:
          </p>
          <div className="space-y-4 text-base text-surface-600 mb-7">
            <div className="flex gap-3">
              <span className="text-surface-300 font-medium">1.</span>
              <span>Choose a connection and resource from the sidebar</span>
            </div>
            <div className="flex gap-3">
              <span className="text-surface-300 font-medium">2.</span>
              <span>
                Look up by ID for a single object, or use Filter to
                query by available fields like status, order number,
                or date range
              </span>
            </div>
            <div className="flex gap-3">
              <span className="text-surface-300 font-medium">3.</span>
              <span>View the full JSON response or a table breakdown</span>
            </div>
          </div>
          <div className="pt-5 border-t border-surface-200/60 space-y-2.5">
            <p className="text-sm text-surface-400">
              <span className="font-medium text-surface-500">Available filters</span>
              {' '}
              vary by resource type.
            </p>
            <p className="text-sm text-surface-400">
              <span className="font-medium text-surface-500">Results are capped at 100.</span>
              {' '}
              Use the Trackstar API directly to paginate through larger datasets.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(row, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      {/* header */}
      <div className="border-b border-surface-200 px-5 py-3 bg-surface-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-surface-900 truncate">
            {getRowTitle(row)}
          </span>
          {row.status && (
            <span className={`badge status-badge ${STATUS_BADGE[row.status] || 'badge-neutral'} text-[10px] py-0 flex-shrink-0`}>
              {STATUS_LABEL[row.status] || row.status}
            </span>
          )}
          {row.id && (
            <MuiTooltip title="View webhook logs for this object" arrow placement="top">
              <IconButton
                component={Link}
                to={`/webhooks?tags=${row.id}`}
                size="small"
                className="ml-auto flex-shrink-0 text-surface-400 hover:text-surface-600 hover:bg-surface-100"
                sx={{ padding: '4px' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
              </IconButton>
            </MuiTooltip>
          )}
        </div>
        {apiPath && (
          <div className="flex items-center gap-1 mt-1 group">
            <span className="font-mono text-[11px] text-surface-400 truncate">
              {`${process.env.REACT_APP_API_ENDPOINT}/${apiPath}/${row.id}`}
            </span>
            <MuiTooltip title={urlCopied ? 'Copied!' : 'Copy URL'} arrow placement="right">
              <IconButton
                size="small"
                onClick={() => {
                  const url = `${process.env.REACT_APP_API_ENDPOINT}/${apiPath}/${row.id}`;
                  navigator.clipboard.writeText(url);
                  setUrlCopied(true);
                  setTimeout(() => setUrlCopied(false), 2000);
                }}
                className="opacity-0 group-hover:opacity-100 text-surface-400 hover:text-surface-600"
                sx={{ padding: '2px' }}
              >
                <ContentCopyIcon sx={{ fontSize: '11px' }} />
              </IconButton>
            </MuiTooltip>
          </div>
        )}
      </div>

      {resourceKey === 'warehouses' && (
        <WarehouseLocations
          warehouseId={row.id}
          connectionId={connectionId}
        />
      )}

      {/* body */}
      <div className="flex-1 overflow-y-scroll px-5 py-4 bg-surface-100/60 relative min-w-0">
        <div className="absolute top-2 right-3 z-10 flex items-center gap-1">
          <div className="flex items-center gap-0.5 border border-surface-200 rounded-md p-0.5 bg-white">
            <MuiTooltip title="JSON" arrow placement="top">
              <button
                type="button"
                onClick={() => setViewMode('json')}
                className={`px-1.5 py-1 rounded text-xs font-mono leading-none transition-colors ${
                  viewMode === 'json'
                    ? 'bg-surface-100 text-surface-900'
                    : 'text-surface-400 hover:text-surface-600'
                }`}
              >
                {'{ }'}
              </button>
            </MuiTooltip>
            <MuiTooltip title="Table" arrow placement="top">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-1.5 py-1 rounded leading-none transition-colors ${
                  viewMode === 'table'
                    ? 'bg-surface-100 text-surface-900'
                    : 'text-surface-400 hover:text-surface-600'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M10 3v18M14 3v18M3 6a3 3 0 013-3h12a3 3 0 013 3v12a3 3 0 01-3 3H6a3 3 0 01-3-3V6z" />
                </svg>
              </button>
            </MuiTooltip>
          </div>
          <IconButton
            size="small"
            onClick={viewMode === 'json' ? toggleExpandAll : undefined}
            className="text-surface-400 hover:text-surface-600 hover:bg-surface-100"
            sx={{ padding: '4px', visibility: viewMode === 'json' ? 'visible' : 'hidden' }}
          >
            {expandState === true ? (
              <UnfoldLessIcon sx={{ fontSize: '14px' }} />
            ) : (
              <UnfoldMoreIcon sx={{ fontSize: '14px' }} />
            )}
          </IconButton>
          <MuiTooltip title={copied ? 'Copied!' : 'Copy JSON'} arrow placement="top">
            <IconButton
              size="small"
              onClick={handleCopy}
              className="text-surface-400 hover:text-surface-600 hover:bg-surface-100"
              sx={{ padding: '4px' }}
            >
              <ContentCopyIcon sx={{ fontSize: '14px' }} />
            </IconButton>
          </MuiTooltip>
        </div>
        {viewMode === 'json' ? (
          <JsonSyntaxHighlighter
            data={row}
            defaultExpandDepth={EXPAND_DEPTH[expandState]}
            expandKey={expandKey}
          />
        ) : (
          <ResourceTable row={row} resourceKey={resourceKey} />
        )}
      </div>
    </div>
  );
}

export default ObjectResponsePanel;
